import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { LoadingSpinner } from './ui';
import { downloadSickNote } from '../services/docuwareService';
import { getErrorMessage } from '../utils/errorMessages';
import AdminEmployeeLeaveModal from './AdminEmployeeLeaveModal';
import './LeaveRequestViewModal.css';

const LeaveRequestViewModal = ({ requestId, onClose, isAdmin = false, isTeamView = false }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingSickNote, setDownloadingSickNote] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  useEffect(() => {
    fetchRequestDetails();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Determine the correct endpoint based on context
      let endpoint;
      if (isAdmin) {
        endpoint = `/api/leave/admin/requests/${requestId}/`;
      } else if (isTeamView) {
        // Use team endpoint for supervised users' leave requests
        endpoint = `/api/leave/team/requests/${requestId}/`;
      } else {
        // Use regular endpoint for own requests
        endpoint = `/api/leave/requests/${requestId}/`;
      }
      
      const response = await axiosInstance.get(endpoint);
      setRequest(response.data);
    } catch (err) {
      console.error('Error fetching request details:', err);
      setError(getErrorMessage(err, 'Failed to load leave request details.'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDayTypeDisplay = (dayType) => {
    const types = {
      'full': 'Full Day',
      'morning': 'Morning Half',
      'afternoon': 'Afternoon Half'
    };
    return types[dayType] || dayType;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#FFC107',
      'approved': '#4CAF50',
      'rejected': '#F44336',
      'cancelled': '#9E9E9E',
      'completed': '#607D8B'
    };
    return colors[status] || '#9E9E9E';
  };

  const getExpectedReturnDate = (endDate, endDayType) => {
    const end = new Date(endDate);
    if (endDayType === 'morning') {
      // If taking morning half of last day, return same day afternoon
      return end.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) + ' (Afternoon)';
    } else {
      // Return next business day
      const nextDay = new Date(end);
      nextDay.setDate(nextDay.getDate() + 1);
      
      // Skip weekends
      while (nextDay.getDay() === 0 || nextDay.getDay() === 6) {
        nextDay.setDate(nextDay.getDate() + 1);
      }
      
      return nextDay.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  const formatDays = (days) => {
    const num = parseFloat(days);
    return num % 1 === 0 ? Math.floor(num) : num.toFixed(1);
  };

  const handleDownloadSickNote = async () => {
    if (!request?.sick_note_docuware_document_id || !request?.sick_note_name) {
      console.error('Missing sick note document ID or name');
      return;
    }
    
    try {
      setDownloadingSickNote(true);
      await downloadSickNote(request.sick_note_docuware_document_id, request.sick_note_name);
    } catch (error) {
      console.error('Error downloading sick note:', error);
      // Could show an error message to the user here
    } finally {
      setDownloadingSickNote(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="leave-view-modal">
          <div className="modal-header">
            <h3>Loading Request Details...</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <LoadingSpinner message="Loading leave request information..." />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="leave-view-modal">
          <div className="modal-header">
            <h3>Error Loading Request</h3>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button className="btn-secondary" onClick={fetchRequestDetails}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="leave-view-modal">
        <div className="modal-header">
          <div className="header-content">
            <h3>Leave Request Details</h3>
            <div className="request-id">Request #{request.id}</div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Employee Information (Admin View) - Compact */}
          {isAdmin && request.employee_details && (
            <div className="compact-employee-section">
              {request.employee_details.avatar_photo && (
                <img 
                  src={request.employee_details.avatar_photo} 
                  alt="Employee Avatar"
                  className="compact-avatar"
                />
              )}
              <div className="compact-employee-details">
                <div className="employee-name">{request.employee_details.name}</div>
                <div className="employee-meta">
                  {request.employee_details.hms_employee_id}
                  {request.employee_details.department && ` • ${request.employee_details.department}`}
                  {request.employee_details.office && ` • ${request.employee_details.office}`}
                </div>
              </div>
            </div>
          )}

          {/* Leave Request Information - Compact Header */}
          <div className="compact-header-section">
            <div className="leave-type-badge-compact" style={{ backgroundColor: request.leave_type_details?.color || '#4CAF50' }}>
              {request.leave_type_details?.name || 'Absent'}
            </div>
            <span className="status-badge-compact" style={{ backgroundColor: getStatusColor(request.status) }}>
              {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
            </span>
          </div>

          {/* Details Grid - 2 Column Layout */}
          <div className="details-grid">
            <div className="detail-item">
              <div className="detail-label">Start Date</div>
              <div className="detail-value">{formatDate(request.start_date)}</div>
              <div className="detail-sub">({getDayTypeDisplay(request.start_day_type)})</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">End Date</div>
              <div className="detail-value">{formatDate(request.end_date)}</div>
              <div className="detail-sub">({getDayTypeDisplay(request.end_day_type)})</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">Total Days</div>
              <div className="detail-value">{formatDays(request.total_days)} days</div>
            </div>

            <div className="detail-item">
              <div className="detail-label">Expected Return</div>
              <div className="detail-value">{getExpectedReturnDate(request.end_date, request.end_day_type)}</div>
            </div>
          </div>

          {/* Reason - Compact */}
          {request.reason && (
            <div className="compact-reason-section">
              <div className="detail-label">Reason for Leave</div>
              <div className="reason-text">{request.reason}</div>
            </div>
          )}

          {/* Sick Note Display - For Sick Leave */}
          {request.sick_note_id && request.leave_type_details?.name === 'Sick Leave' && (
            <div className="sick-note-display-section">
              <div className="detail-label">Doctor's Sick Note</div>
              <div className="sick-note-card">
                <div className="sick-note-card-header">
                  <i className="fas fa-file-medical" style={{marginRight: '8px', color: '#4CAF50'}}></i>
                  <span className="sick-note-card-title">Medical Certificate Attached</span>
                </div>
                <div className="sick-note-card-body">
                  <div className="sick-note-info-row">
                    <span className="sick-note-info-label">File:</span>
                    <span className="sick-note-info-value">{request.sick_note_name}</span>
                  </div>
                  {request.sick_note_uploaded_at && (
                    <div className="sick-note-info-row">
                      <span className="sick-note-info-label">Uploaded:</span>
                      <span className="sick-note-info-value">
                        {formatDate(request.sick_note_uploaded_at)}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="sick-note-download-btn"
                  onClick={handleDownloadSickNote}
                  disabled={downloadingSickNote}
                >
                  <i className="fas fa-download" style={{marginRight: '6px'}}></i>
                  {downloadingSickNote ? 'Downloading...' : 'Download Sick Note'}
                </button>
              </div>
            </div>
          )}

          {/* Admin Review Information - Compact */}
          {isAdmin && (request.reviewed_by_name || request.admin_notes) && (
            <div className="compact-admin-section">
              <div className="detail-label">Admin Review</div>
              <div className="admin-review-grid">
                {request.reviewed_by_name && (
                  <div className="review-item">
                    <span className="review-label">Reviewed By:</span>
                    <span className="review-value">{request.reviewed_by_name}</span>
                  </div>
                )}
                {request.reviewed_at && (
                  <div className="review-item">
                    <span className="review-label">Reviewed At:</span>
                    <span className="review-value">{formatDateTime(request.reviewed_at)}</span>
                  </div>
                )}
              </div>
              {request.admin_notes && (
                <div className="admin-notes-text">{request.admin_notes}</div>
              )}
            </div>
          )}

          {/* Return to Work Information */}
          {request.return_to_work_confirmed && (
            <div className="info-section rtw-section">
              <h4>Return to Work</h4>
              <div className="rtw-info">
                <div className="info-item">
                  <label>Status:</label>
                  <span className="rtw-status confirmed">Confirmed</span>
                </div>
                {request.return_to_work_date && (
                  <div className="info-item">
                    <label>Actual Return Date:</label>
                    <span>{formatDate(request.return_to_work_date)}</span>
                  </div>
                )}
                {request.return_to_work_notes && (
                  <div className="rtw-notes">
                    <label>Return Notes:</label>
                    <div className="notes-text">{request.return_to_work_notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Information */}
          <div className="info-section system-section">
            <h4>Request Timeline</h4>
            <div className="timeline-info">
              <div className="info-item">
                <label>Submitted:</label>
                <span>{formatDateTime(request.created_at)}</span>
              </div>
              {request.updated_at !== request.created_at && (
                <div className="info-item">
                  <label>Last Updated:</label>
                  <span>{formatDateTime(request.updated_at)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Manage Leave Balance entry point (admins + supervisors).
              Backend enforces supervision; this is a UI shortcut so they
              don't have to navigate elsewhere to fix entitlement. In team
              view the server says whether this viewer may manage it. */}
          {(isAdmin || (isTeamView && request.can_manage_balance)) && (
            <div className="leave-view-manage-balance">
              <button
                type="button"
                className="leave-view-manage-balance-btn"
                onClick={() => setShowBalanceModal(true)}
              >
                Manage Leave Balance
              </button>
            </div>
          )}
        </div>
      </div>

      {showBalanceModal && (
        <AdminEmployeeLeaveModal
          employee={
            // AdminLeaveRequestSerializer gives us employee_details (full object).
            // TeamLeaveRequestSerializer only gives us the FK id + name/avatar, so
            // we construct the minimal shape AdminEmployeeLeaveModal needs.
            isAdmin && request.employee_details
              ? request.employee_details
              : {
                  id: request.employee,
                  display_name: request.employee_name,
                  avatar_photo: request.employee_avatar,
                }
          }
          onClose={() => setShowBalanceModal(false)}
        />
      )}
    </div>
  );
};

export default LeaveRequestViewModal;

